import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getIngestionJob,
  listIngestionJobs,
  retryIngestionJob,
} from "../../../apiIngestionJobs";
import {
  ingestionJobKey,
  isInMotionStatus,
  normalizeIngestionJob,
} from "./ingestionPresentation";

const VISIBLE_POLL_MS = 1500;
const HIDDEN_POLL_MS = 10000;
const COMPLETED_VISIBLE_MS = 6000;

const IngestionJobsContext = createContext(null);

const values = (record) => Object.values(record);

function isRecentCompletion(job, now = Date.now()) {
  if (job.status !== "completed" || !job.finishedAt) return false;
  const finishedAt = new Date(job.finishedAt).getTime();
  return Number.isFinite(finishedAt) && now - finishedAt < COMPLETED_VISIBLE_MS;
}

export function IngestionJobsProvider({ children, onCompleted }) {
  const [jobsByKey, setJobsByKey] = useState({});
  const [visibilityVersion, setVisibilityVersion] = useState(0);
  const [activeSourcesVersion, setActiveSourcesVersion] = useState(0);
  const activeSourcesRef = useRef(new Map());
  const loadedSourcesRef = useRef(new Set());
  const completionRefreshQueuedRef = useRef(false);

  const queueCompletionRefresh = useCallback(() => {
    if (!onCompleted || completionRefreshQueuedRef.current) return;
    completionRefreshQueuedRef.current = true;
    Promise.resolve().then(() => {
      completionRefreshQueuedRef.current = false;
      onCompleted();
    });
  }, [onCompleted]);

  const mergeJob = useCallback((rawJob, metadata = {}) => {
    const incoming = normalizeIngestionJob(rawJob, metadata);
    if (!incoming.jobId || !incoming.resourceType || incoming.resourceId === null) return;
    const key = ingestionJobKey(incoming.resourceType, incoming.resourceId);

    setJobsByKey((current) => {
      const previous = current[key];
      if (metadata.expectedJobId && previous?.jobId !== metadata.expectedJobId) {
        return current;
      }
      if (incoming.status === "superseded") {
        if (!previous || previous.jobId !== incoming.jobId) return current;
        const updated = { ...current };
        delete updated[key];
        return updated;
      }
      const nextJob = {
        ...previous,
        ...incoming,
        label: incoming.label || previous?.label || null,
        pollErrorCount: 0,
      };
      if (previous?.jobId === nextJob.jobId && previous?.status !== "completed" && nextJob.status === "completed") {
        queueCompletionRefresh();
      }
      return { ...current, [key]: nextJob };
    });
  }, [queueCompletionRefresh]);

  const restoreSource = useCallback(async (sourceDbId) => {
    const sourceKey = sourceDbId === null ? "*" : String(sourceDbId);
    if (loadedSourcesRef.current.has(sourceKey)) return;
    loadedSourcesRef.current.add(sourceKey);

    try {
      const [activeResponse, recentResponse] = await Promise.all([
        listIngestionJobs(sourceDbId, true),
        listIngestionJobs(sourceDbId, false),
      ]);
      if (!activeSourcesRef.current.has(sourceKey)) return;

      (activeResponse?.data || []).forEach((job) => mergeJob(job));
      (recentResponse?.data || [])
        .map((job) => normalizeIngestionJob(job))
        .filter((job) => isRecentCompletion(job))
        .forEach((job) => mergeJob(job));
    } catch {
      loadedSourcesRef.current.delete(sourceKey);
    }
  }, [mergeJob]);

  const activateSource = useCallback((sourceDbId) => {
    const normalizedSourceId = sourceDbId === undefined || sourceDbId === "" ? null : sourceDbId;
    const sourceKey = normalizedSourceId === null ? "*" : String(normalizedSourceId);
    activeSourcesRef.current.set(sourceKey, (activeSourcesRef.current.get(sourceKey) || 0) + 1);
    setActiveSourcesVersion((value) => value + 1);
    restoreSource(normalizedSourceId);

    return () => {
      const nextCount = (activeSourcesRef.current.get(sourceKey) || 1) - 1;
      if (nextCount <= 0) activeSourcesRef.current.delete(sourceKey);
      else activeSourcesRef.current.set(sourceKey, nextCount);
      setActiveSourcesVersion((value) => value + 1);
    };
  }, [restoreSource]);

  const isSourceActive = useCallback((job) => (
    activeSourcesRef.current.has("*")
    || activeSourcesRef.current.has(String(job.sourceDbId))
  ), []);

  const registerJob = useCallback(({ summary, sourceDbId, resourceId, label }) => {
    if (!summary?.job_id) return null;
    mergeJob(summary, { sourceDbId, resourceId, label });
    return summary.job_id;
  }, [mergeJob]);

  const retryJob = useCallback(async (jobId) => {
    const previous = values(jobsByKey).find((job) => job.jobId === String(jobId));
    if (!previous) return null;
    const response = await retryIngestionJob(jobId);
    const next = normalizeIngestionJob(response?.data || {}, { label: previous.label });
    const previousKey = ingestionJobKey(previous.resourceType, previous.resourceId);
    const nextKey = ingestionJobKey(next.resourceType, next.resourceId);
    setJobsByKey((current) => {
      const updated = { ...current };
      delete updated[previousKey];
      updated[nextKey] = { ...next, label: previous.label, pollErrorCount: 0 };
      return updated;
    });
    return next;
  }, [jobsByKey]);

  const pollJobs = useCallback(async (jobs, isCancelled = () => false) => {
    await Promise.all(jobs.map(async (job) => {
      try {
        const response = await getIngestionJob(job.jobId);
        if (!isCancelled()) {
          mergeJob(response?.data || {}, { expectedJobId: job.jobId, label: job.label });
        }
      } catch {
        if (isCancelled()) return;
        const key = ingestionJobKey(job.resourceType, job.resourceId);
        setJobsByKey((current) => {
          if (current[key]?.jobId !== job.jobId) return current;
          return {
            ...current,
            [key]: {
              ...current[key],
              pollErrorCount: (current[key].pollErrorCount || 0) + 1,
            },
          };
        });
      }
    }));
  }, [mergeJob]);

  useEffect(() => {
    const handleVisibility = () => {
      setVisibilityVersion((value) => value + 1);
      if (document.visibilityState === "visible") {
        const activeJobs = values(jobsByKey).filter(
          (job) => isInMotionStatus(job.status) && isSourceActive(job),
        );
        pollJobs(activeJobs);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [activeSourcesVersion, isSourceActive, jobsByKey, pollJobs]);

  useEffect(() => {
    const activeJobs = values(jobsByKey).filter(
      (job) => isInMotionStatus(job.status) && isSourceActive(job),
    );
    if (activeJobs.length === 0) return undefined;

    let cancelled = false;
    const delay = document.visibilityState === "hidden" ? HIDDEN_POLL_MS : VISIBLE_POLL_MS;
    const timer = setTimeout(async () => {
      await pollJobs(activeJobs, () => cancelled);
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeSourcesVersion, isSourceActive, jobsByKey, pollJobs, visibilityVersion]);

  useEffect(() => {
    const completedJobs = values(jobsByKey).filter((job) => job.status === "completed");
    if (completedJobs.length === 0) return undefined;
    const timers = completedJobs.map((job) => {
      const finishedAt = job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now();
      const remaining = Math.max(0, COMPLETED_VISIBLE_MS - (Date.now() - finishedAt));
      return setTimeout(() => {
        const key = ingestionJobKey(job.resourceType, job.resourceId);
        setJobsByKey((current) => {
          if (current[key]?.jobId !== job.jobId || current[key]?.status !== "completed") return current;
          const updated = { ...current };
          delete updated[key];
          return updated;
        });
      }, remaining);
    });
    return () => timers.forEach(clearTimeout);
  }, [jobsByKey]);

  const contextValue = useMemo(() => ({
    jobs: values(jobsByKey),
    activateSource,
    registerJob,
    retryJob,
  }), [activateSource, jobsByKey, registerJob, retryJob]);

  return (
    <IngestionJobsContext.Provider value={contextValue}>
      {children}
    </IngestionJobsContext.Provider>
  );
}

export function useIngestionJobs({ sourceDbId = null, resourceTypes = null } = {}) {
  const context = useContext(IngestionJobsContext);
  if (!context) {
    throw new Error("useIngestionJobs must be used within IngestionJobsProvider");
  }

  const { activateSource } = context;
  useEffect(() => activateSource(sourceDbId), [activateSource, sourceDbId]);

  const allowedTypes = useMemo(
    () => resourceTypes ? new Set(resourceTypes) : null,
    [resourceTypes],
  );
  const jobs = useMemo(() => context.jobs.filter((job) => {
    if (sourceDbId !== null && sourceDbId !== undefined && String(job.sourceDbId) !== String(sourceDbId)) {
      return false;
    }
    return !allowedTypes || allowedTypes.has(job.resourceType);
  }), [allowedTypes, context.jobs, sourceDbId]);

  return {
    jobs,
    inMotionCount: jobs.filter((job) => isInMotionStatus(job.status)).length,
    needsAttentionCount: jobs.filter((job) => job.status === "failed").length,
    getJob: (resourceType, resourceId) =>
      jobs.find((job) => ingestionJobKey(job.resourceType, job.resourceId) === ingestionJobKey(resourceType, resourceId)) || null,
    registerJob: context.registerJob,
    retryJob: context.retryJob,
  };
}
